<template>
  <div class="container-sm" style="padding-top: 1rem">
    <div class="case-creation__step case-creation__Case-information">
      <div class="case-creation__card">
        <div class="case-creation__heading">
          <div class="title-with-icon">
            <span class="icon">
              <img
                src="@/assets/images/icons/icon-case-info.svg"
                alt="icon"
                class="img-fluid"
              />
            </span>
            <h3 class="title">Potential Payers</h3>
          </div>
        </div>

        <div class="case-creation__body" v-loading="loading">
          <!-- Lab selection -->
          <div class="lab-row">
            <el-row :gutter="15">
              <el-col :xl="10" :lg="10" :md="12" :sm="16" :xs="24">
                <h4 class="name">Lab</h4>
                <el-select
                  v-model="selectedLab"
                  filterable
                  placeholder="Select lab"
                  style="width: 100%"
                  @change="onLabChange"
                >
                  <el-option
                    v-for="lab in getUserAllowedLabs"
                    :key="lab._id"
                    :label="lab.name"
                    :value="lab._id"
                  />
                </el-select>
              </el-col>
            </el-row>
          </div>

          <!-- Add potential payer (restricted to allowed users) -->
          <div class="add-row" v-if="canAddPotentialPayer">
            <el-row :gutter="15" align="middle">
              <el-col :xl="10" :lg="10" :md="10" :sm="12" :xs="24">
                <h4 class="name">Payer</h4>
                <el-select
                  v-model="selectedPayor"
                  filterable
                  remote
                  reserve-keyword
                  clearable
                  placeholder="Search payer from insurance payors"
                  :remote-method="searchPayors"
                  :loading="payorLoading"
                  value-key="_id"
                  style="width: 100%"
                >
                  <el-option
                    v-for="payor in insurancePayorOptions"
                    :key="payor._id"
                    :label="payor.name"
                    :value="payor"
                  />
                </el-select>
              </el-col>
              <el-col :xl="5" :lg="5" :md="5" :sm="6" :xs="12">
                <h4 class="name">Max Limit</h4>
                <el-input-number
                  v-model="splitLimit"
                  :min="1"
                  :max="100"
                  controls-position="right"
                  style="width: 100%"
                />
              </el-col>
              <el-col :xl="4" :lg="4" :md="4" :sm="6" :xs="12">
                <el-button
                  type="primary"
                  :disabled="!selectedPayor"
                  :loading="adding"
                  style="margin-top: 22px"
                  @click="addPayer"
                  >Add</el-button
                >
              </el-col>
            </el-row>
          </div>

          <!-- Potential payers table -->
          <el-table
            :data="potentialPayers"
            style="width: 100%; margin-top: 1rem"
            row-key="_id"
          >
            <el-table-column type="expand">
              <template #default="scope">
                <div class="accessions-wrap">
                  <h4 class="name">Accessions</h4>
                  <el-table
                    v-if="scope.row.split_accessions && scope.row.split_accessions.length"
                    :data="scope.row.split_accessions"
                    size="small"
                  >
                    <el-table-column label="Accession ID" prop="accession_id" />
                    <el-table-column label="Order ID" prop="order_id" />
                    <el-table-column label="Date">
                      <template #default="s">
                        {{ momentWithTimezoneTime(s.row.at, "MM-DD-yyyy hh:mm A") || "--" }}
                      </template>
                    </el-table-column>
                  </el-table>
                  <p v-else class="value">No accessions split yet.</p>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Payer" prop="payor_name" min-width="180" />
            <el-table-column label="Progress" min-width="140">
              <template #default="scope">
                {{ scope.row.split_count || 0 }} / {{ scope.row.split_limit }}
              </template>
            </el-table-column>
            <el-table-column label="Status" min-width="120">
              <template #default="scope">
                <el-tag :type="statusTagType(scope.row.status)">{{
                  scope.row.status
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column
              v-if="canAddPotentialPayer"
              label="Actions"
              min-width="200"
            >
              <template #default="scope">
                <el-button
                  v-if="scope.row.status === 'ACTIVE'"
                  size="small"
                  @click="changeStatus(scope.row, 'INACTIVE')"
                  >Deactivate</el-button
                >
                <el-button
                  v-if="scope.row.status === 'INACTIVE'"
                  size="small"
                  type="success"
                  @click="changeStatus(scope.row, 'ACTIVE')"
                  >Activate</el-button
                >
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-wrap" v-if="total > limit">
            <el-pagination
              layout="prev, pager, next"
              :total="total"
              :page-size="limit"
              :current-page="page"
              @current-change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

// Only these users may add potential payers.
const ALLOWED_ADD_USERNAMES = ["sathwikb", "anilurs"];

export default {
  computed: {
    // getAuthenticatedUser is provided globally via the GlobalHelper mixin.
    ...mapGetters("potentialPayers", [
      "getPotentialPayers",
      "getInsurancePayors",
    ]),
    canAddPotentialPayer() {
      const username = this.getAuthenticatedUser?.username;
      return !!username && ALLOWED_ADD_USERNAMES.includes(username);
    },
  },
  mounted() {
    // getUserPrimaryLabId / getUserAllowedLabs come from the global GlobalHelper mixin.
    this.selectedLab =
      this.getUserPrimaryLabId || this.getUserAllowedLabs?.[0]?._id || "";
    this.fetchPayers();
    this.searchPayors("");
  },
  data() {
    return {
      loading: false,
      adding: false,
      payorLoading: false,
      selectedLab: "",
      selectedPayor: null,
      splitLimit: 6,
      potentialPayers: [],
      insurancePayorOptions: [],
      page: 1,
      limit: 10,
      total: 0,
    };
  },
  methods: {
    statusTagType(status) {
      switch (status) {
        case "ACTIVE":
          return "success";
        case "COMPLETED":
          return "warning";
        default:
          return "info";
      }
    },
    onLabChange() {
      this.page = 1;
      this.fetchPayers();
    },
    async fetchPayers() {
      if (!this.selectedLab) return;
      try {
        this.loading = true;
        await this.$store.dispatch("potentialPayers/fetchPotentialPayers", {
          lab: this.selectedLab,
          params: { page: this.page, limit: this.limit },
        });
        const res = this.getPotentialPayers || {};
        this.potentialPayers = res.data || [];
        this.total = res.total || 0;
      } catch (err) {
        console.log(err);
      } finally {
        this.loading = false;
      }
    },
    async searchPayors(query) {
      try {
        this.payorLoading = true;
        await this.$store.dispatch("potentialPayers/fetchInsurancePayors", {
          page: 1,
          limit: 20,
          search_string: query || "",
        });
        this.insurancePayorOptions = this.getInsurancePayors?.data || [];
      } catch (err) {
        console.log(err);
      } finally {
        this.payorLoading = false;
      }
    },
    async addPayer() {
      if (!this.selectedPayor) return;
      try {
        this.adding = true;
        await this.$store.dispatch("potentialPayers/addPotentialPayer", {
          lab: this.selectedLab,
          payor_name: this.selectedPayor.name,
          insurance_payor: this.selectedPayor._id,
          split_limit: this.splitLimit,
        });
        this.$message.success("Potential payer added successfully");
        this.selectedPayor = null;
        this.splitLimit = 6;
        await this.fetchPayers();
      } catch (err) {
        // error surfaced via errors module; show fallback message
        const msg =
          err?.response?.data?.message || "Failed to add potential payer";
        this.$message.error(msg);
      } finally {
        this.adding = false;
      }
    },
    async changeStatus(row, status) {
      try {
        this.loading = true;
        await this.$store.dispatch(
          "potentialPayers/updatePotentialPayerStatus",
          { id: row._id, status, lab: this.selectedLab }
        );
        this.$message.success("Status updated successfully");
        await this.fetchPayers();
      } catch (err) {
        this.$message.error("Failed to update status");
      } finally {
        this.loading = false;
      }
    },
    onPageChange(p) {
      this.page = p;
      this.fetchPayers();
    },
  },
};
</script>

<style lang="scss" scoped>
.lab-row {
  padding: 4px 0 8px;
}
.add-row {
  padding: 10px 0 4px;
  border-top: 1px solid #eef0f4;
}
.accessions-wrap {
  padding: 10px 20px;
}
.name {
  font-size: 15px;
  color: rgb(148, 126, 126);
  font-weight: 500;
  display: block;
  margin-bottom: 6px;
}
.value {
  color: #0a2342;
  font-size: 13px;
  font-weight: 500;
}
.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}
</style>
